import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Calendar, MapPin, Sparkles, ArrowRight, Palette, Music, Camera } from "lucide-react";
import api from "@/lib/api";

const HIGHLIGHTS = [
  { icon: Palette, label: "Visual Arts", desc: "Local masters and rising stars across painting, sculpture, and mixed-media." },
  { icon: Music,   label: "Live Performance", desc: "Reggae, jazz, dancehall, and afrobeats curated nightly." },
  { icon: Camera,  label: "Photography", desc: "Caribbean portrait & street photography exhibits." },
];

export default function LiguaneaArtFestivalPage() {
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/events").then((r) => {
      const list = r.data?.events || r.data || [];
      // Prefer an event tagged 'liguanea' or with the keyword in the title
      const found = list.find((e) =>
        (e.tags || []).some((t) => String(t).toLowerCase().includes("liguanea")) ||
        String(e.title || "").toLowerCase().includes("liguanea")
      );
      setEvent(found || null);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  return (
    <div data-testid="liguanea-page" className="bg-luxury-black min-h-screen">
      {/* HERO */}
      <section className="relative pt-32 pb-24 sm:pt-44 sm:pb-32 overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <div className="absolute inset-0 bg-gradient-to-br from-luxury-black via-[#1a0f00] to-luxury-black" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_40%,rgba(212,175,55,0.18),transparent_55%)]" />
        </div>
        <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-24">
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <p className="text-xs font-body uppercase tracking-[0.3em] text-gold mb-4">Annual Caribbean Showcase</p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-7xl font-light tracking-tight text-white leading-[1.05]">
              Liguanea <span className="italic text-gold">Art Festival</span>
            </h1>
            <p className="mt-6 max-w-2xl text-white/70 font-body text-base sm:text-lg">
              A weekend where Liguanea becomes Kingston's open-air gallery — visual art, live music,
              culinary popups, and a private collector's circle hosted by Aristocrats Signature Events.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <Link to="/booking" data-testid="liguanea-book-btn"
                className="inline-flex items-center gap-2 bg-gold text-luxury-black px-6 py-3 font-body text-sm uppercase tracking-wider hover:bg-gold/90 transition-colors">
                Reserve a Pass <ArrowRight size={14} />
              </Link>
              <Link to="/portfolio" className="text-white/70 hover:text-gold font-body text-sm transition-colors">
                See last year's recap →
              </Link>
            </div>
            {event && (
              <div data-testid="liguanea-live-event" className="mt-10 inline-flex flex-wrap items-center gap-4 p-4 border border-gold/30 bg-gold/5 rounded-md text-sm font-body">
                <span className="flex items-center gap-1 text-gold"><Calendar size={14} /> {event.date || "Coming soon"}</span>
                <span className="flex items-center gap-1 text-white/80"><MapPin size={14} /> {event.location || "Liguanea, Kingston"}</span>
                <span className="text-white/60">{event.title}</span>
              </div>
            )}
            {!event && !loading && (
              <p className="mt-10 text-white/40 text-xs font-body italic">Date announcement landing soon — join the VIP list to be notified first.</p>
            )}
          </motion.div>
        </div>
      </section>

      {/* HIGHLIGHTS */}
      <section className="py-20 sm:py-28 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-24">
          <div className="mb-12">
            <p className="text-xs font-body uppercase tracking-[0.2em] text-gold mb-2 inline-flex items-center gap-1.5">
              <Sparkles size={12} /> What's on
            </p>
            <h2 className="font-heading text-3xl sm:text-4xl font-light text-white">Three days. <span className="italic text-gold">Three currents</span> of culture.</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {HIGHLIGHTS.map((h, i) => (
              <motion.div key={h.label} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.08 }}
                className="p-6 border border-white/10 bg-white/[0.02] hover:border-gold/30 transition-colors">
                <h.icon className="text-gold mb-4" size={28} />
                <h3 className="font-heading text-xl text-white mb-1">{h.label}</h3>
                <p className="text-white/60 font-body text-sm">{h.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 border-t border-white/5">
        <div className="max-w-4xl mx-auto px-6 sm:px-12 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl font-light text-white">Be on the <span className="italic text-gold">collector's list</span>.</h2>
          <p className="mt-4 text-white/60 font-body text-sm">Aristocrats handles VIP access, private viewings, and after-hours invitations to the festival.</p>
          <Link to="/booking" data-testid="liguanea-cta-btn"
            className="mt-8 inline-flex items-center gap-2 bg-gold text-luxury-black px-6 py-3 font-body text-sm uppercase tracking-wider hover:bg-gold/90 transition-colors">
            Reserve a VIP Experience <ArrowRight size={14} />
          </Link>
        </div>
      </section>
    </div>
  );
}
